import { useState, type FormEvent } from 'react';
import { isTimedOut, TIMEOUT_DURATIONS } from '@clanfire/shared';
import { serverApi } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit } from '@/hooks/useForm';
import { formatLater } from '@/lib/time';
import { cn } from '@/lib/utils';
import { memberName, useServers } from '@/stores/servers';
import { toast } from '@/stores/ui';
import { useUser } from '@/stores/users';

/** Time a member out (or end their timeout early). Needs Time Out Members. */
export function TimeoutModal({ serverId, userId, onClose }: { serverId: string; userId: string; onClose: () => void }) {
  const user = useUser(userId);
  const name = memberName(serverId, userId) ?? user?.displayName ?? 'this member';
  const current = useServers((s) => s.members[serverId]?.[userId]?.timeoutUntil ?? null);
  const active = isTimedOut(current);
  const [seconds, setSeconds] = useState<number>(TIMEOUT_DURATIONS[2].seconds);
  const [reason, setReason] = useState('');
  const { loading, error, run } = useSubmit();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const member = await run(() => serverApi.timeout(serverId, userId, seconds, reason.trim() || undefined));
    if (!member) return;
    useServers.getState().upsertMember(member);
    toast.success(`${name} is timed out until ${formatLater(member.timeoutUntil!)}.`);
    onClose();
  };

  const lift = async () => {
    const member = await run(() => serverApi.removeTimeout(serverId, userId));
    if (!member) return;
    useServers.getState().upsertMember(member);
    toast.success(`${name}'s timeout has ended.`);
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Time out ${name}`}
      description={
        active
          ? `${name} is timed out until ${formatLater(current!)}. Pick a new length to change it.`
          : `${name} will be able to read, but not send messages, react, or join voice until it ends.`
      }
      size="sm"
      footer={
        <>
          {active && (
            <Button variant="ghost" className="mr-auto" disabled={loading} onClick={() => void lift()}>
              Remove timeout
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="timeout-form" variant="danger" loading={loading}>
            Time out
          </Button>
        </>
      }
    >
      <form id="timeout-form" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <fieldset>
          <legend className="mb-2 pl-1 text-[13px] font-semibold text-fg-soft">How long?</legend>
          <div className="grid grid-cols-3 gap-2">
            {TIMEOUT_DURATIONS.map((d) => (
              <label
                key={d.seconds}
                className={cn(
                  'flex h-10 cursor-pointer items-center justify-center rounded-control border text-sm font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40',
                  seconds === d.seconds ? 'border-accent bg-accent-soft text-fg' : 'border-line text-fg-soft hover:bg-hover',
                )}
              >
                <input type="radio" name="duration" value={d.seconds} checked={seconds === d.seconds} onChange={() => setSeconds(d.seconds)} className="sr-only" />
                {d.label}
              </label>
            ))}
          </div>
        </fieldset>
        <Input label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={512} placeholder="Shown in the audit log" />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
